import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { IconLogout } from '../components/icons';
import { useToast } from '../components/Toast';
import { ConfirmDialog } from '../components/Modal';
import { Field, Loading, Segmented, TextArea, TextInput } from '../components/ui';
import { messageOf } from '../lib/format';
import { readTheme, themeOptions, writeTheme, type ThemeMode } from '../lib/theme';
import { LOCALE_NAMES, t, useLocale, writeLocale, type Locale } from '../i18n';

function ProfileForm() {
  const { user, applyUser } = useAuth();
  const toast = useToast();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmHandle, setConfirmHandle] = useState(false);

  const handleChanged = username.trim().toLowerCase() !== user?.username;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.users.updateMe({
        displayName: displayName.trim(),
        username: username.trim().toLowerCase(),
        bio,
        avatarUrl: avatarUrl.trim(),
      });
      applyUser(updated);
      toast.success(t('settings.profileUpdated'));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
      setConfirmHandle(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!displayName.trim()) {
      setError(t('settings.nameRequired'));
      return;
    }
    if (handleChanged) setConfirmHandle(true);
    else void save();
  }

  return (
    <form className="panel stack" onSubmit={onSubmit}>
      <Field label={t('settings.displayName')} htmlFor="s-name" hint={t('settings.displayNameHint')}>
        <TextInput
          id="s-name"
          value={displayName}
          maxLength={60}
          invalid={Boolean(error)}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </Field>
      <Field
        label={t('settings.username')}
        htmlFor="s-username"
        hint={t('settings.usernameHint')}
        error={error ?? undefined}
      >
        <TextInput
          id="s-username"
          value={username}
          autoComplete="username"
          spellCheck={false}
          invalid={Boolean(error)}
          onChange={(event) => setUsername(event.target.value)}
        />
      </Field>
      <Field label={t('settings.bio')} htmlFor="s-bio" hint={t('settings.bioHint')}>
        <TextArea id="s-bio" rows={3} value={bio} maxLength={280} onChange={(event) => setBio(event.target.value)} />
      </Field>
      <Field label={t('settings.avatar')} htmlFor="s-avatar" hint={t('settings.avatarHint')}>
        <TextInput
          id="s-avatar"
          type="url"
          inputMode="url"
          placeholder="https://"
          value={avatarUrl}
          onChange={(event) => setAvatarUrl(event.target.value)}
        />
      </Field>
      <div className="row row--between">
        <span className="tiny muted">
          {user?.email}
        </span>
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? t('item.saving') : t('item.save')}
        </button>
      </div>

      <ConfirmDialog
        open={confirmHandle}
        title={t('settings.changeHandleTitle')}
        description={t('settings.changeHandleBody', { username: user?.username ?? '' })}
        confirmLabel={t('settings.changeHandleYes')}
        busy={busy}
        onClose={() => setConfirmHandle(false)}
        onConfirm={() => void save()}
      />
    </form>
  );
}

function AppearanceForm() {
  const locale = useLocale();
  const [mode, setMode] = useState<ThemeMode>(readTheme());
  const options = themeOptions();
  const option = options.find((item) => item.value === mode);

  return (
    <div className="panel stack">
      <Segmented
        name="theme"
        label={t('settings.theme')}
        value={mode}
        onChange={(next) => {
          setMode(next);
          writeTheme(next);
        }}
        options={options.map((item) => ({ value: item.value, label: item.label }))}
      />
      <p className="field__hint">{option?.hint}</p>
      <Segmented
        name="language"
        label={t('settings.language')}
        value={locale}
        onChange={(next) => writeLocale(next)}
        options={(Object.keys(LOCALE_NAMES) as Locale[]).map((value) => ({ value, label: LOCALE_NAMES[value] }))}
      />
      <p className="field__hint">{t('settings.languageHint')}</p>
    </div>
  );
}

function PasswordForm() {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (next !== repeat) {
      setError(t('settings.passwordMismatch'));
      return;
    }
    if (next.length < 8) {
      setError(t('settings.passwordShort'));
      return;
    }
    setBusy(true);
    try {
      await api.users.changePassword(current, next);
      setCurrent('');
      setNext('');
      setRepeat('');
      toast.success(t('settings.passwordChanged'));
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={onSubmit}>
      <Field label={t('settings.currentPassword')} htmlFor="s-current">
        <TextInput
          id="s-current"
          type="password"
          autoComplete="current-password"
          value={current}
          invalid={Boolean(error)}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </Field>
      <Field label={t('settings.newPassword')} htmlFor="s-next" hint={t('settings.passwordHint')} error={error ?? undefined}>
        <TextInput
          id="s-next"
          type="password"
          autoComplete="new-password"
          value={next}
          invalid={Boolean(error)}
          onChange={(event) => setNext(event.target.value)}
        />
      </Field>
      <Field label={t('settings.repeatPassword')} htmlFor="s-repeat">
        <TextInput
          id="s-repeat"
          type="password"
          autoComplete="new-password"
          value={repeat}
          invalid={Boolean(error)}
          onChange={(event) => setRepeat(event.target.value)}
        />
      </Field>
      <div className="row">
        <button type="submit" className="btn btn--outline" disabled={busy || !current || !next}>
          {busy ? t('settings.changing') : t('settings.changePassword')}
        </button>
      </div>
    </form>
  );
}

export function SettingsPage() {
  const { user, status, logout } = useAuth();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState(false);

  if (status === 'loading' || !user) {
    return (
      <div className="page">
        <Loading rows={4} />
      </div>
    );
  }

  async function signOut() {
    await logout();
    navigate('/', { replace: true });
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t('nav.settings')}</h1>
          <p className="page-head__sub">
            {t('settings.profileLinkNote')}{' '}
            <Link to={`/u/${user.username}`}>/u/{user.username}</Link>
          </p>
        </div>
      </div>

      <div className="settings-grid">
        <section className="stack-sm" aria-labelledby="set-profile">
          <h2 id="set-profile">{t('nav.profile')}</h2>
          <ProfileForm />
        </section>

        <section className="stack-sm" aria-labelledby="set-theme">
          <h2 id="set-theme">{t('settings.sectionAppearance')}</h2>
          <AppearanceForm />
        </section>

        <section className="stack-sm" aria-labelledby="set-password">
          <h2 id="set-password">{t('settings.sectionSecurity')}</h2>
          <PasswordForm />
        </section>

        <section className="stack-sm" aria-labelledby="set-session">
          <h2 id="set-session">{t('settings.sectionSession')}</h2>
          <div className="danger-zone stack-sm">
            <p className="small dim">{t('settings.sessionNote')}</p>
            <div className="row">
              <button type="button" className="btn btn--outline" onClick={() => setLeaving(true)}>
                <IconLogout size={17} /> {t('settings.logout')}
              </button>
            </div>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={leaving}
        title={t('settings.logoutTitle')}
        description={t('settings.logoutBody')}
        confirmLabel={t('settings.logout')}
        danger={false}
        onClose={() => setLeaving(false)}
        onConfirm={() => void signOut()}
      />
    </div>
  );
}
